import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { createUser } from "../../api/userApi";
import { createHealthData } from "../../api/healthDataApi";
import { saveUserProfile } from "../../api/userProfileApi";
import { createMedicalCondition } from "../../api/medicalConditionApi";
import { createMedicalHistory } from "../../api/medicalHistoryApi";
import { createSurgery } from "../../api/surgeryApi";

const Registration = () => {

  const [step, setStep] = useState(1);
  const [agreed, setAgreed] = useState(false);
  const [dropdownOpenCurrent, setDropdownOpenCurrent] = useState(false);
  const [dropdownOpenPrevious, setDropdownOpenPrevious] = useState(false);
  const [dropdownOpenFamily, setDropdownFamily] = useState(false);
  const [dropdownOpenSurgery, setDropdownOpenSurgery] = useState(false);

  const [selectedCurrentConditions, setSelectedCurrentConditions] = useState([]);
  const [selectedPreviousConditions, setSelectedPreviousConditions] = useState([]);
  const [selectedFamilyConditions, setSelectedFamilyConditions] = useState([]);
  const [selectedSurgery, setSelectedSurgery] = useState([]);

  const [otherCurrentCondition, setOtherCurrentCondition] = useState("");
  const [otherPreviousCondition, setOtherPreviousCondition] = useState("");
  const [otherFamilyCondition, setOtherFamilyCondition] = useState("");
  const [otherSurgery, setOtherSurgery] = useState("");

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    dob: "",
    password: "",
  });

  const navigate = useNavigate();

  const allCurrentConditions = [
    "Hypertension (High Blood Pressure)",
    "Coronary Artery Disease",
    "Heart Failure",
    "Atrial Fibrillation",
    "Stroke",
    "Arrhythmia",
    "Angina",
    "Peripheral Artery Disease",
    "Deep Venous Thrombosis (DVT)",
    "Venous Insufficiency",
    "Varicose Veins",
    "Asthma",
    "Chronic Obstructive Pulmonary Disease (COPD)",
    "Emphysema",
    "Bronchitis",
    "Pneumonia",
    "Sleep Apnea",
    "Pulmonary Hypertension",
    "Type 1 Diabetes",
    "Type 2 Diabetes",
    "Hypothyroidism (Underactive Thyroid)",
    "Hyperthyroidism (Overactive Thyroid)",
    "Adrenal Insufficiency",
    "Cushing’s Syndrome",
    "Alzheimer’s Disease",
    "Parkinson’s Disease",
    "Epilepsy",
    "Multiple Sclerosis",
    "Migraine",
    "Seizures",
    "Irritable Bowel Syndrome (IBS)",
    "Celiac Disease",
    "Crohn’s Disease",
    "Ulcerative Colitis",
    "Gallstones",
    "Liver Disease",
    "Osteoarthritis",
    "Rheumatoid Arthritis",
    "Gout",
    "Osteoporosis",
    "Back Pain (acute)",
    "Back Pain (chronic)",
    "Fibromyalgia",
    "Depression",
    "Anxiety",
    "Bipolar Disorder",
    "Schizophrenia",
    "PTSD (Post-Traumatic Stress Disorder)",
    "OCD (Obsessive-Compulsive Disorder)",
    "HIV/AIDS",
    "Tuberculosis",
    "Hepatitis (A, B, C)",
    "Influenza",
    "COVID-19",
    "Measles",
    "Breast Cancer",
    "Lung Cancer",
    "Prostate Cancer",
    "Colon Cancer",
    "Skin Cancer (Melanoma, Non-Melanoma)",
    "Leukemia",
    "Lymphoma",
    "Eczema",
    "Psoriasis",
    "Rosacea",
    "Acne",
    "Chronic Kidney Disease",
    "End-Stage Renal Disease",
    "Polycystic Kidney Disease",
    "Kidney Stones",
    "Other"
  ];

  const allHistoryConditions = [
    "Heart Attack (Myocardial Infarction)",
    "Angina (Chest Pain)",
    "Heart Surgery (e.g., Bypass, Stent)",
    "Arrhythmia (Irregular Heartbeat)",
    "Stroke or Transient Ischemic Attack (TIA)",
    "Lung Cancer",
    "Liver Disease",
    "Chronic Bronchitis",
    "Asthma",
    "High Cholesterol (Hyperlipidemia)",
    "Depression",
    "Thyroid Disorder",
    "Diabetes (Type 1 or Type 2)",
    "Pneumonia",
    "Chronic Fatigue Syndrome",
    "HIV/AIDS",
    "Hepatitis",
    "Autoimmune Disease",
    "Endometriosis",
    "Epilepsy",
    "Gallbladder Disease",
    "Polycystic Ovary Syndrome (PCOS)",
    "Osteoarthritis",
    "Rheumatoid Arthritis",
    "Anxiety Disorder",
    "PTSD (Post-Traumatic Stress Disorder)",
    "Leukemia",
    "Skin Cancer",
    "Chronic Kidney Disease (CKD)",
    "Other"
  ];

  const allFamilyConditions = [
    { label: "Cardiovascular Conditions", isCategory: true },
    { label: "Heart Disease" },
    { label: "Hypertension" },
    { label: "Stroke or Transcient Ischemic Attack (TIA)" },
    { label: "Coronary Artery Disease" },
    { label: "Aneurysm (Abdominal, Thoracic)" },
    { label: "Peripheral Artery Disease" },

    { label: "Cancer", isCategory: true },
    { label: "Breast Cancer" },
    { label: "Colon Cancer" },
    { label: "Lung Cancer" },
    { label: "Prostate Cancer" },
    { label: "Ovarian Cancer" },
    { label: "Skin Cancer" },
    { label: "Leukemia" },
    { label: "Lymphoma" },
    { label: "Kidney Cancer" },

    { label: "Endocrine Disorders", isCategory: true },
    { label: "Diabetes (Type 1 or Type 2)" },
    { label: "Thyroid Disease (Hyperthyroidism, Hypothyroidism)" },
    { label: "Obesity" },
    { label: "Cushing’s Syndrome" },
    { label: "Polycystic Ovary Syndrome (PCOS)" },

    { label: "Musculoskeletal Disorders", isCategory: true },
    { label: "Osteoarthritis" },
    { label: "Rheumatoid Arthritis" },
    { label: "Gout" },
    { label: "Osteoporosis" },
    { label: "Scoliosis" },

    { label: "Mental Health Disorders", isCategory: true },
    { label: "Depression" },
    { label: "Anxiety" },
    { label: "Bipolar Disorder" },
    { label: "Schizophrenia" },
    { label: "Substance Abuse Disorders" },
    { label: "ADHD (Attention Deficit Hyperactivity Disorder)" },

    { label: "Genetic Conditions", isCategory: true },
    { label: "Cystic Fibrosis " },
    { label: " Sickle Cell Anemia" },
    { label: "Hemophilia" },
    { label: " Down Syndrome" },
    { label: "Marfan Syndrome " },
    { label: "Tay-Sachs Disease" },
    { label: "Huntington's Disease" },

    { label: "Other Conditions", isCategory: true },
    { label: "Asthma" },
    { label: "High Cholesterol" },
    { label: "Autoimmune Disease" },
    { label: "Allergies" },
    { label: "Alcoholism" },

    { label: "Other" }
  ];

  const allSurgeries = [
    "Coronary Artery Bypass Grafting (CABG)",
    "Heart Valve Replacement/Repair",
    "Pacemaker Implantation",
    "Angioplasty (Balloon) or Stent Insertion",
    "Aneurysm Repair (Abdominal, Thoracic)",
    "Appendectomy (Appendix Removal)",
    "Gallbladder Removal (Cholecystectomy)",
    "Hernia Repair",
    "Bowel Resection (Intestinal Surgery)",
    "Gastric Bypass or Weight Loss Surgery",
    "Colon Surgery",
    "Hip Replacement",
    "Knee Replacement",
    "Spinal Fusion Surgery",
    "Rotator Cuff Repair",
    "Joint Replacement (Shoulder, Ankle)",
    "Carpal Tunnel Surgery",
    "Cataract Surgery",
    "Breast Reconstruction Surgery",
    "Nephrectomy (Kidney Removal)",
    "Prostatectomy (Prostate Removal)",
    "Hysterectomy (Uterus Removal)",
    "C-section (Cesarean Section)",
    "Amputation (Partial or Complete)",
    "Other"
  ]

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const cleanedCurrentConditions = [
      ...selectedCurrentConditions.filter(c => c !== "Other"),
      ...(otherCurrentCondition.trim() ? [otherCurrentCondition.trim()] : [])
    ];

    const cleanedPreviousConditions = [
      ...selectedPreviousConditions.filter(c => c !== "Other"),
      ...(otherPreviousCondition.trim() ? [otherPreviousCondition.trim()] : [])
    ];

    const cleanedSurgery = [
      ...selectedSurgery.filter(c => c !== "Other"),
      ...(otherSurgery.trim() ? [otherSurgery.trim()] : [])
    ];

    const cleanedFamilyConditions = [
      ...selectedFamilyConditions.filter(c => c !== "Other"),
      ...(otherFamilyCondition.trim() ? [otherFamilyCondition.trim()] : [])
    ];


    // create user object with form data
    try {
      const user = await createUser(formData);

      const healthData = {
        user_id: user.userId,
        family_history: cleanedFamilyConditions.join(","),
        allergies: "",
      }

      await createHealthData(healthData);

      if (cleanedCurrentConditions.length > 0) {
        for (let condition of cleanedCurrentConditions) {
          await createMedicalCondition({
            user_id: user.userId,
            condition_name: condition,
          });
        }
      }

      if (cleanedPreviousConditions.length > 0) {
        for (let condition of cleanedPreviousConditions) {
          await createMedicalHistory({
            user_id: user.userId,
            condition_name: condition,
          });
        }
      }

      if (cleanedSurgery.length > 0) {
        for (let surgery of cleanedSurgery) {
          await createSurgery({
            user_id: user.userId,
            surgery_name: surgery,
          });
        }
      }

      const userProfileData = {
        user_id: user.userId,
        full_name: `${formData.first_name} ${formData.last_name}`,
        email: formData.email,
        phone_number: formData.phone_number
      }

      await saveUserProfile(userProfileData);

      // reset form data
      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        phone_number: "",
        dob: "",
        password: "",
      });

      localStorage.setItem("userId", user.userId);
      navigate("/dashboard");
    } catch (error) {
      console.error("Error creating user:", error);
    }

    // reset all selections
    setSelectedCurrentConditions([]);
    setSelectedPreviousConditions([]);
    setSelectedFamilyConditions([]);
    setSelectedSurgery([]);
    setAgreed(false);
  };

  return (
    <form onSubmit={handleSubmit} className="flex items-center justify-center min-h-screen bg-gray-100">

      {step === 1 && (
        <div className="flex justify-center p-6">
          <div className="w-full max-w-2xl rounded-2xl p-8 border border-slate-300 space-y-6">
            <h2 className="text-2xl font-bold text-[#04395b]">Personal Information</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="block text-sm font-medium mb-1">First Name:
                <input type="text" name="first_name" value={formData.first_name} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-[#04395b]" required />
              </label>
              <label className="block text-sm font-medium mb-1">Last Name:
                <input type="text" name="last_name" value={formData.last_name} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-[#04395b]" required />
              </label>

              <label className="block text-sm font-medium mb-1">Email:
                <input type="email" name="email" value={formData.email} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-[#04395b]" required />
              </label>
              <label className="block text-sm font-medium mb-1">Phone Number:
                <input type="tel" name="phone_number" value={formData.phone_number} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-[#04395b]" required />
              </label>

              <label className="block text-sm font-medium mb-1">Date of Birth:
                <input type="date" name="dob" value={formData.dob} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-[#04395b]" required />
              </label>
              <label className="block text-sm font-medium mb-1">Password:
                <input type="password" name="password" value={formData.password} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-[#04395b]" required />
              </label>
            </div>

            <div className="flex justify-between pt-4">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="px-6 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400"
              >
                Back
              </button>
              <button
                type="button"
                onClick={() => setStep((prev) => prev + 1)}
                className="px-6 py-2 bg-[#04395b] text-white rounded-md hover:bg-[#032d45]"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}


      {step == 2 && (
        <div className="flex justify-center p-6">
          <div className="w-full max-w-2xl rounded-2xl p-8 border border-slate-300 space-y-8">
            <h2 className="text-2xl font-bold text-[#04395b]">Medical Information</h2>
            <div>
              <label className="block text-md font-semibold mb-1">Current Medical Conditions</label>
              <div className="text-sm text-gray-600 mb-2"> Please list only current medical conditions you have been diagnosed with.</div>
              <div className="relative">
                <button
                  type="button"
                  className="w-full px-4 py-2 text-left bg-white border border-gray-300 rounded-md shadow-sm"
                  onClick={() => setDropdownOpenCurrent(!dropdownOpenCurrent)}
                >
                  {selectedCurrentConditions.length === 0
                    ? "Select conditions"
                    : selectedCurrentConditions.slice(0, 2).join(", ") +
                    (selectedCurrentConditions.length > 2
                      ? ` +${selectedCurrentConditions.length - 2} more`
                      : "")}
                </button>

                {dropdownOpenCurrent && (
                  <div className="absolute z-10 w-full mt-1 overflow-y-auto bg-white border border-gray-300 rounded shadow max-h-60">
                    {allCurrentConditions.map((condition) => (
                      <label
                        key={condition}
                        className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          className="shrink-0 w-4 h-4 accent-blue-600"
                          checked={selectedCurrentConditions.includes(condition)}
                          onChange={() => {
                            setSelectedCurrentConditions((prev) =>
                              prev.includes(condition)
                                ? prev.filter((c) => c !== condition)
                                : [...prev, condition]
                            );
                            if (condition === "Other" && selectedCurrentConditions.includes("Other")) {
                              setOtherCurrentCondition("");
                            }
                          }}
                        />
                        {condition}
                      </label>

                    ))}
                  </div>
                )}

                {selectedCurrentConditions.includes("Other") && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700">Please specify:</label>
                    <input
                      type="text"
                      value={otherCurrentCondition}
                      onChange={(e) => setOtherCurrentCondition(e.target.value)}
                      className="w-full px-4 py-2 text-left bg-white border border-gray-300 rounded-md shadow-sm"
                      placeholder="Enter other medical condition"
                    />
                  </div>
                )}
              </div>
            </div>


            <div>
              <label className="block text-md font-semibold mb-1">Medical History</label>
              <p className="text-sm text-gray-600 mb-2">Please only list past resolved medical issues.</p>
              <div className="relative">
                <button
                  type="button"
                  className="w-full px-4 py-2 text-left bg-white border border-gray-300 rounded-md shadow-sm"
                  onClick={() => setDropdownOpenPrevious(!dropdownOpenPrevious)}
                >
                  {selectedPreviousConditions.length === 0
                    ? "Select conditions"
                    : selectedPreviousConditions.slice(0, 2).join(", ") +
                    (selectedPreviousConditions.length > 2
                      ? ` +${selectedPreviousConditions.length - 2} more`
                      : "")}
                </button>

                {dropdownOpenPrevious && (
                  <div className="absolute z-10 w-full mt-1 overflow-y-auto bg-white border border-gray-300 rounded shadow max-h-60">
                    {allHistoryConditions.map((condition) => (
                      <label key={condition} className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer">
                        <input
                          type="checkbox"
                          className="shrink-0 w-4 h-4 accent-blue-600"
                          checked={selectedPreviousConditions.includes(condition)}
                          onChange={() => {
                            setSelectedPreviousConditions((prev) =>
                              prev.includes(condition)
                                ? prev.filter((c) => c !== condition)
                                : [...prev, condition]
                            );
                            if (condition === "Other" && selectedPreviousConditions.includes("Other")) {
                              setOtherPreviousCondition("");
                            }
                          }}
                        />
                        {condition}
                      </label>
                    ))}
                  </div>
                )}

                {selectedPreviousConditions.includes("Other") && (
                  <div className="mt-2">
                    <label className="block text-sm font-medium text-gray-700">Please specify:</label>
                    <input
                      type="text"
                      value={otherPreviousCondition}
                      onChange={(e) => setOtherPreviousCondition(e.target.value)}
                      className="w-full px-4 py-2 text-left bg-white border border-gray-300 rounded-md shadow-sm"
                      placeholder="Enter other medical condition"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="space-x-6 align-center">
              <button
                type="button"
                onClick={() => setStep((prev) => prev - 1)} disabled={step === 5}
                className="px-6 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400"
              >
                Back
              </button>
              <button
                type="button"
                onClick={() => setStep((prev) => prev + 1)} disabled={step === 5}
                className="px-6 py-2 bg-[#04395b] text-white rounded-md hover:bg-[#032d45]"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {step == 3 && (
        <div className="flex flex-col justify-center p-4">
          <div className="w-full max-w-2xl rounded-2xl p-8 border border-slate-300">

            <h2 className="mb-6 text-2xl font-bold text-[#04395b]">Medical Information (Continued)</h2>

            <div>
              <label className="block text-md font-semibold mb-1">Family History</label>
              <div className="text-sm text-gray-600 mb-1"> Please only list any family medical history.</div>
              <div className="relative mb-4">
                <button
                  type="button"
                  className="w-full px-4 py-2 text-left bg-white border border-gray-300 rounded-md shadow-sm"
                  onClick={() => setDropdownFamily(!dropdownOpenFamily)}
                >
                  {selectedFamilyConditions.length === 0
                    ? "Select conditions"
                    : selectedFamilyConditions.slice(0, 2).join(", ") +
                    (selectedFamilyConditions.length > 2
                      ? ` +${selectedFamilyConditions.length - 2} more`
                      : "")}
                </button>

                {dropdownOpenFamily && (
                  <div className="absolute z-10 w-full mt-1 overflow-y-auto bg-white border rounded shadow max-h-60">
                    {allFamilyConditions.map(({ label, isCategory }) =>
                      isCategory ? (
                        <div key={label} className="px-4 py-2 font-bold text-gray-700 bg-gray-100">
                          {label}
                        </div>
                      ) : (
                        <label key={label} className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer">
                          <input
                            type="checkbox"
                            className="shrink-0 w-4 h-4 accent-blue-600"
                            checked={selectedFamilyConditions.includes(label)}
                            onChange={() => {
                              setSelectedFamilyConditions((prev) =>
                                prev.includes(label)
                                  ? prev.filter((c) => c !== label)
                                  : [...prev, label]
                              );
                              if (label === "Other" && selectedFamilyConditions.includes("Other")) {
                                setOtherFamilyCondition("");
                              }
                            }}
                          />
                          {label}
                        </label>
                      )
                    )}
                  </div>
                )}


                {selectedFamilyConditions.includes("Other") && (
                  <div className="mt-2">
                    <label className="block text-sm font-medium text-gray-700">Please specify:</label>
                    <input
                      type="text"
                      value={otherFamilyCondition}
                      onChange={(e) => setOtherFamilyCondition(e.target.value)}
                      className="w-full px-4 py-2 text-left bg-white border border-gray-300 rounded-md shadow-sm"
                      placeholder="Enter other medical condition"
                    />
                  </div>
                )}
              </div>
            </div>



            <div>
              <label className="block text-md font-semibold mb-1">Surgeries</label>
              <div className="text-sm text-gray-600 mb-1"> List any surgical operations done on you.</div>
              <div className="relative mb-4">
                <button
                  type="button"
                  className="w-full px-4 py-2 text-left bg-white border border-gray-300 rounded-md shadow-sm"
                  onClick={() => setDropdownOpenSurgery(!dropdownOpenSurgery)}
                >
                  {selectedSurgery.length === 0
                    ? "Select conditions"
                    : selectedSurgery.slice(0, 2).join(", ") +
                    (selectedSurgery.length > 2
                      ? ` +${selectedSurgery.length - 2} more`
                      : "")}
                </button>

                {dropdownOpenSurgery && (
                  <div className="absolute z-10 w-full mt-1 overflow-y-auto bg-white border rounded shadow max-h-60">
                    {allSurgeries.map((condition) => (
                      <label key={condition} className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer">
                        <input
                          type="checkbox"
                          className="shrink-0 w-4 h-4 accent-blue-600"
                          checked={selectedSurgery.includes(condition)}
                          onChange={() => {
                            setSelectedSurgery((prev) =>
                              prev.includes(condition)
                                ? prev.filter((c) => c !== condition)
                                : [...prev, condition]
                            );
                            if (condition === "Other" && selectedSurgery.includes("Other")) {
                              setOtherPreviousCondition("");
                            }
                          }}
                        />
                        {condition}
                      </label>
                    ))}
                  </div>
                )}

                {selectedSurgery.includes("Other") && (
                  <div className="mt-2">
                    <label className="block text-sm font-medium text-gray-700">Please specify:</label>
                    <input
                      type="text"
                      value={otherSurgery}
                      onChange={(e) => setOtherSurgery(e.target.value)}
                      className="w-full px-4 py-2 text-left bg-white border border-gray-300 rounded-md shadow-sm"
                      placeholder="Enter other medical condition"
                    />
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-md font-semibold mb-1">Free Text Box</label>
              <div className="text-sm text-gray-600 mb-1"> List anything else that has not been mentioned.</div>
              <textarea id="message" rows="4" className="block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500"></textarea>
            </div>


            <div className="space-x-6 align-center mt-5">
              <button
                type="button"
                onClick={() => setStep((prev) => prev - 1)} disabled={step === 5}
                className="px-6 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400"
              >
                Back
              </button>
              <button
                type="button"
                onClick={() => setStep((prev) => prev + 1)} disabled={step === 5}
                className="px-6 py-2 bg-[#04395b] text-white rounded-md hover:bg-[#032d45]"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="flex flex-col items-center p-4">
          <div className="w-full max-w-2xl rounded-2xl p-8 border border-slate-300">
            <h2 className="mb-6 text-2xl font-bold text-[#04395b]">HIPAA Waiver</h2>

            {/* Waiver Text */}
            <div className="h-48 p-4 overflow-y-scroll text-sm border rounded bg-gray-50">
              <p>
                Placeholder HIPPAA waiver text. This is where the actual waiver text would go, explaining the terms and conditions of the HIPAA waiver. It should be clear and concise, outlining the rights of the patient and the responsibilities of the healthcare provider.
              </p>
            </div>

            {/* Agreement Checkbox */}
            <div className="mt-4">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700 text-nowrap">
                <input
                  type="checkbox"
                  id="agree"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="accent-blue-600"
                />
                I agree to the HIPAA waiver
              </label>
            </div>

            {/* Navigation Buttons */}
            <div className="mt-6 flex justify-between">
              <button
                type="button"
                onClick={() => setStep((prev) => prev - 1)}
                className="px-6 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400"
              >
                Back
              </button>

              <button
                type="submit"
                disabled={!agreed}
                className={`px-6 py-2 rounded ${agreed
                  ? 'bg-[#04395b] hover:bg-[#032d45] text-white'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

    </form>

  );
}

export default Registration;